/** @jsxImportSource @emotion/react */
import { Header, useSession } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  Input,
  Modal,
  Select,
  Typography,
  useTheme,
  ToggleButtonGroup,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { KycDocumentImages } from 'components';
import { KeyDownAutoFocus } from 'components/KeyDownAutoFocus';
import {
  updateUser,
  getKycDocumentImage,
  getKycDocumentImages,
} from 'tabs/NewSkinning/SkinningUsers/api';
import { useSelected } from '../../context';
import { AccountStatus, getLabel, VerificationStatus } from '../../types';
import { History } from './components/History';
import { OnboardingTable } from './components/OnboardingTable';
import { RetriggerOnboarding } from './components/ReTriggerOnboarding';
import { Transactions } from './components/Transactions';

const parseIntOrZeroToString = (value?: string | null) =>
  (parseInt(value ?? '0') || 0).toString();

type Tab = 'details' | 'transactions' | 'history';

export const SkinningUserDetails = () => {
  const session = useSession();
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();
  const user = useSelected();

  const [tab, setTab] = useState<Tab>('details');
  const [riskScoreAdjustment, setRiskScoreAdjustment] = useState<string | null>(
    null
  );
  const [status, setStatus] = useState<AccountStatus>(AccountStatus.ACTIVE);
  const [sourceOfFundsVerificationStatus, setSourceOfFundsVerificationStatus] =
    useState<VerificationStatus>();

  const hasEditPermission =
    session.authenticated && session.permissions.includes('edit_qb_users');

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCustomerInfoModal, setShowCustomerInfoModal] = useState(false);

  const [{ loading }, doUpdate] = useAsyncFn(async () => {
    if (!user || !user.externalReference) return;

    const response = await updateUser({
      userReference: user.externalReference,
      status,
      sourceOfFundsVerificationStatus,
      riskScoreAdjustment:
        parseIntOrZeroToString(
          user.kyc.riskScoreAdjustment
            ? user.kyc.riskScoreAdjustment.toString()
            : null
        ) !== parseIntOrZeroToString(riskScoreAdjustment)
          ? parseInt(parseIntOrZeroToString(riskScoreAdjustment))
          : undefined,
    });
    setShowUpdateModal(false);
    return response;
  }, [status, user, riskScoreAdjustment, sourceOfFundsVerificationStatus]);

  useEffect(() => {
    if (!user || user.userId !== userId) {
      return;
    }

    setStatus(user.status);
    setRiskScoreAdjustment(
      parseIntOrZeroToString(
        user.kyc.riskScoreAdjustment
          ? user.kyc.riskScoreAdjustment.toString()
          : null
      )
    );
    setSourceOfFundsVerificationStatus(
      user.kyc.sourceOfFundsVerificationStatus
    );
  }, [user, userId]);

  const hasChanges =
    user &&
    (user.status !== status ||
      parseIntOrZeroToString(
        user.kyc.riskScoreAdjustment
          ? user.kyc.riskScoreAdjustment.toString()
          : null
      ) !== parseIntOrZeroToString(riskScoreAdjustment) ||
      user.kyc.sourceOfFundsVerificationStatus !==
        sourceOfFundsVerificationStatus);

  const hasOnboardingHistory =
    user && Object.keys(user.kyc.onboarding).length > 0;

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: spacing.xxs,
            alignItems: 'center',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/new-skinning/users')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Skinning Users
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{user && user.userId}</Typography>
          </div>
          {hasEditPermission && (
            <UtilityButton
              loading={loading}
              text="Update"
              onClick={() => setShowUpdateModal(true)}
              disabled={!hasChanges}
            />
          )}
        </div>
      </Header>
      <Card>
        <div css={{ marginBottom: spacing.xxs }}>
          <ToggleButtonGroup
            onChange={(option) => setTab(option as Tab)}
            selected={tab}
            toggleOptions={[
              { label: 'Details', value: 'details' },
              { label: 'Transactions', value: 'transactions' },
              { label: 'History', value: 'history' },
            ]}
          />
        </div>
        {tab === 'details' && (
          <div>
            <div css={{ display: 'grid', gap: spacing.xxs }}>
              {user && (
                <>
                  <Select
                    label={getLabel('status')}
                    options={Object.keys(AccountStatus).map((s) => ({
                      label: s,
                      value: s,
                    }))}
                    values={[status]}
                    onSelect={(s) => setStatus(s[0] as AccountStatus)}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        hasEditPermission &&
                        hasChanges
                      ) {
                        setShowUpdateModal(true);
                      }
                    }}
                  />
                  <Input
                    type="number"
                    label={getLabel('riskScoreAdjustment')}
                    value={riskScoreAdjustment ?? ''}
                    onChange={(e) => setRiskScoreAdjustment(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        hasEditPermission &&
                        hasChanges
                      ) {
                        setShowUpdateModal(true);
                      }
                    }}
                  />
                  <Select
                    label={getLabel('sourceOfFundsVerificationStatus')}
                    options={Object.keys(VerificationStatus).map((s) => ({
                      label: s,
                      value: s,
                    }))}
                    values={
                      sourceOfFundsVerificationStatus
                        ? [sourceOfFundsVerificationStatus]
                        : undefined
                    }
                    onSelect={(s) =>
                      setSourceOfFundsVerificationStatus(
                        s[0] as VerificationStatus
                      )
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        hasEditPermission &&
                        hasChanges
                      ) {
                        setShowUpdateModal(true);
                      }
                    }}
                  />
                </>
              )}
              {user &&
                Object.entries({
                  ...user,
                  ...user.kyc,
                  identities: user.kyc.identities
                    .map((identity) => {
                      return identity.eidProviderType;
                    })
                    .join(', '),
                })
                  .filter(
                    ([k]) =>
                      ![
                        'status',
                        'riskScoreAdjustment',
                        'sourceOfFundsVerificationStatus',
                        'intergiroKyc',
                        'addressStreetLine2',
                        'kyc',
                        'onboarding',
                        'interactions',
                        'id',
                      ].includes(k)
                  )
                  .sort()
                  .map(([k, v]) => {
                    return (
                      <div
                        css={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: palette.grey.default,
                          },
                        }}
                        key={k}
                      >
                        <Input
                          type={
                            v && typeof v === 'string' && v.length > 100
                              ? 'textarea'
                              : 'text'
                          }
                          label={getLabel(k)}
                          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                          value={(v as string) ?? '-'}
                          onChange={() => ({})}
                          disabled
                        />
                      </div>
                    );
                  })}
              {hasOnboardingHistory && (
                <Card>
                  <Typography variant="bodySmallest" color={palette.grey.x400}>
                    Onboarding
                  </Typography>
                  <OnboardingTable
                    onboarding={user.kyc.onboarding.onboarding}
                  />
                </Card>
              )}

              <div
                css={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: spacing.xxs,
                }}
              >
                <UtilityButton
                  css={{ marginLeft: spacing.xxs }}
                  text="Customer info"
                  onClick={() => {
                    setShowCustomerInfoModal(true);
                  }}
                />
                {hasOnboardingHistory && (
                  <RetriggerOnboarding userReference={user.externalReference} />
                )}
              </div>
              {user && (
                <KycDocumentImages
                  userId={user.userId}
                  getKycDocumentImage={getKycDocumentImage}
                  getKycDocumentImages={getKycDocumentImages}
                />
              )}
            </div>
          </div>
        )}
        {tab === 'history' && user && user.externalReference && (
          <History
            key={user.externalReference}
            userReference={user.externalReference}
          />
        )}
        {tab === 'transactions' && user && (
          <Transactions userId={user.userId} />
        )}
      </Card>

      {showUpdateModal && (
        <Modal
          title="Are you sure you want to update this user?"
          closeModal={() => !loading && setShowUpdateModal(false)}
          primaryButtonProps={{
            onClick: () => {
              void doUpdate();
            },
            text: 'Update',
            loading: loading,
          }}
          secondaryButtonProps={{
            onClick: () => setShowUpdateModal(false),
            text: 'Cancel',
            disabled: loading,
          }}
        >
          <KeyDownAutoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') void doUpdate();
            }}
          />
        </Modal>
      )}
      {showCustomerInfoModal && user && (
        <Modal
          title="Customer information"
          subtitle="Easy way for FCU to copy important customer info"
          closeModal={() => setShowCustomerInfoModal(false)}
        >
          <div>
            <Typography>
              Business relationship established:{' '}
              {new Date(user.createdAt).toLocaleDateString('sv-se')}
            </Typography>
            <Typography>
              Full name: {user.firstName} {user.lastName}
            </Typography>
            <Typography>Email: {user.emailAddress}</Typography>
            <Typography>Phone number: {user.phoneNumber}</Typography>
            <Typography>Customer ID: {user.userId}</Typography>
            <Typography>
              Social security number / date of birth: {user.personalIdNumber}
            </Typography>
            <Typography>
              Address: {user.addressStreetLine1}, {user.addressPostalCode}{' '}
              {user.addressCity}, {user.addressCountryCode}
            </Typography>
          </div>
        </Modal>
      )}
    </>
  );
};
